import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useBlocker } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  HeroActions,
  HeroBar,
  HeroBody,
  HeroControlRow,
  HeroMeta,
  HeroMetaAction,
  HeroMetaSeparator,
  HeroTitle,
  ViewTab,
  ViewTabList,
  backLinkVariants,
  buttonVariants,
  cn,
  typePillVariants,
} from '@wundo/ui';
import { ChevronLeft, Video } from 'lucide-react';
import {
  renderMeetingReport,
  type MeetingCorrectionScope,
  type MeetingPageDocument,
  type MeetingPageOfflineRecording,
  type MeetingPagePrep,
  type MeetingPageResponse,
  type MeetingPrepContent,
  type MeetingReportSection,
  type MeetingTemplateScope,
} from '@wundo/shared/meetingPage.ts';
import {
  withCurrentTemplate,
  type MeetingTypeOption,
} from '@/lib/withCurrentTemplate.ts';
import { MeetingAttendeesDialog } from './MeetingAttendeesDialog.tsx';
import { ManualMeetingDeleteDialog } from './ManualMeetingDeleteDialog.tsx';
import { ManualMeetingDialog, type ManualMeetingValues } from './ManualMeetingDialog.tsx';
import { MeetingDocumentDeleteDialog } from './MeetingDocumentDeleteDialog.tsx';
import { MeetingMoreControl } from './MeetingMoreControl.tsx';
import { MeetingNotesControl, type MeetingOfflineOffer } from './MeetingNotesControl.tsx';
import { MeetingPrepPanel } from './MeetingPrepPanel.tsx';
import { OfflineRecordingBanner } from './OfflineRecordingBanner.tsx';
import { NotetakerIssueBanner } from './NotetakerIssueBanner.tsx';
import { MeetingShareNotesDialog } from './MeetingShareNotesDialog.tsx';
import { OfflineRecordingCapDialog } from './OfflineRecordingCapDialog.tsx';
import { OfflineRecordingControl } from './OfflineRecordingControl.tsx';
import { OfflineRecordingLeaveDialog } from './OfflineRecordingLeaveDialog.tsx';
import { OfflineRecordingLiveDialog } from './OfflineRecordingLiveDialog.tsx';
import { OfflineRecordingReplaceDialog } from './OfflineRecordingReplaceDialog.tsx';
import { OfflineRecordingResumeDialog } from './OfflineRecordingResumeDialog.tsx';
import { OfflineRecordingStartDialog } from './OfflineRecordingStartDialog.tsx';
import { MeetingRegenerateDialog } from './MeetingRegenerateDialog.tsx';
import { MeetingRemoveDialog } from './MeetingRemoveDialog.tsx';
import { MeetingReportPanel, type MeetingReportWaiting } from './MeetingReportPanel.tsx';
import { MeetingSourcesControl } from './MeetingSourcesControl.tsx';
import { MeetingTemplateSwitchDialog } from './MeetingTemplateSwitchDialog.tsx';
import { MeetingStaffControl } from './MeetingStaffControl.tsx';
import { MeetingTopicJudgmentDialog } from './MeetingTopicJudgmentDialog.tsx';
import { MeetingTranscriptDialog } from './MeetingTranscriptDialog.tsx';
import { MeetingTypeControl } from './MeetingTypeControl.tsx';
import { ScopeDialog } from './ScopeDialog.tsx';
import { meetingAttendeeList, type MeetingAttendeeRow } from './meetingAttendeeList.ts';
import { meetingPageTitle } from './meetingPageTitle.ts';
import { formatDayAndMonth, formatTime } from '@/components/agenda/meetingFormat.ts';
import { wundoScope } from '@/lib/addToWundo.ts';
import { storedMicrophoneId } from '@/lib/audioDevices.ts';
import { fetchCurrentUser } from '@wundo/client/auth.ts';
import { useImpersonatedBy } from '@wundo/client/impersonation.ts';
import { fetchColleagues } from '@/lib/colleagues.ts';
import {
  addMeetingActionItem,
  correctMeeting,
  deleteMeetingActionItem,
  deleteMeetingMaterial,
  deleteThreadDocument,
  fetchMeetingNote,
  generateMeetingPrep,
  generateMeetingReport,
  reportMeetingDocumentOpened,
  saveMeetingNote,
  saveMeetingPrep,
  saveMeetingReport,
  setMeetingTemplate,
  updateMeetingActionItem,
  uploadThreadDocument,
} from '@/lib/meetingPage.ts';
import { deleteManualMeeting, updateManualMeeting } from '@/lib/meetings.ts';
import { fetchMeetingTemplates } from '@wundo/client/memberMeetingTemplates.ts';
import { meetingPageQuery } from '@/lib/meetingPageQuery.ts';
import { buildRichTextEditorWiring } from '@/lib/richTextEditor.ts';
import { useOfflineRecording } from '@/lib/useOfflineRecording.ts';
import { m } from '@/paraglide/messages.js';

/* How long "Gekopieerd" stays on the button before it goes back to normal. */
const COPIED_FOR_MS = 2000;

/* How often the page asks again while a verslag is being written for it. */
const REPORT_POLL_MS = 5000;
/* The notetaker's leg runs minutes to half an hour after the meeting, not seconds. */
const NOTETAKER_POLL_MS = 15_000;
/* The notetaker can knock a few minutes before the meeting starts. */
const NOTETAKER_EARLY_MS = 5 * 60 * 1000;

/*
 * Every way off this page, while a recording runs. Where the user was going
 * does not change what leaving costs, so nothing about the destination is
 * looked at. Held out here so the router is not resubscribed on every render.
 */
const LEAVING_COSTS_THE_RECORDING = () => true;

/* What the page assumes about microphone recording while it is still loading. */
const NO_OFFLINE_RECORDING: MeetingPageOfflineRecording = {
  status: 'idle',
  started_at: null,
  next_chunk_index: 0,
};

/*
 * Where the notetaker is being moved to, while the scope question is open.
 * Null means no question is open.
 */
type PendingFlip = boolean | null;

/*
 * The gesprekstype the user picked, while the page is still asking about it.
 * `scope` is null while the first question — this meeting or the whole
 * conversation — is still on screen, and holds the answer once it is given.
 */
interface PendingTemplate {
  template: MeetingTypeOption;
  scope: MeetingTemplateScope | null;
}
/**
 * Which question about starting a recording is on screen. A meeting that was
 * already recorded gets `replace` first — what a second recording costs — and
 * every recording gets `setup`, the microphone and the bar that proves it.
 */
type RecordAsk = 'replace' | 'setup' | null;

export type MeetingPanel = 'prep' | 'report';
type Panel = MeetingPanel;

/* Which document to write, and whether to write over the one that is there. */
interface GenerateRequest {
  panel: Panel;
  regenerate: boolean;
}

/* Which document is waiting for a "yes, again". */
interface RegenerateAsk {
  panel: Panel;
}

export interface MeetingViewProps {
  meetingId: string;
  /*
   * Which of the two documents opens first. A verslag notification links
   * straight to the verslag; without a say, the preparation leads.
   */
  initialPanel?: MeetingPanel;
  /*
   * Which of the two documents is open, told to whoever put this view on
   * screen. The page uses it to keep the chat beside it on the same subject.
   */
  onPanelChange?: (panel: MeetingPanel) => void;
  /*
   * Nothing left to keep the user here: the meeting was deleted. Whoever put
   * this view on screen takes it off — the page goes back to the week —
   * because refetching a deleted meeting only draws "could not load this
   * meeting" at somebody who deleted it on purpose.
   */
  onLeave?: () => void;
}

/**
 * One meeting, whole: the bar that names it, the row of what it is set to, and
 * the two documents under them.
 *
 * This is the meeting page, as a component rather than a route: the chat stands
 * beside it, and the page has to fold for the width it is left with — so it
 * asks its own container how wide it is (`@container`) rather than the window,
 * which has not changed.
 *
 * A meeting Wundo stays out of gets the same view — details, and the offer to
 * add it. That empty page is how a one-off customer meeting gets Wundo's help,
 * so it is not a lesser version of the page but its starting point.
 */
export function MeetingView({
  meetingId,
  initialPanel = 'prep',
  onPanelChange,
  onLeave,
}: MeetingViewProps) {
  const queryClient = useQueryClient();
  const [flip, setFlip] = useState<PendingFlip>(null);
  /*
   * Which of the two views is open. It belongs to the meeting, not to the data:
   * every action here invalidates the page, and a tab that reset on a refetch
   * would take the user off the verslag they were writing. Both homes render
   * this component keyed by meeting id, so nothing short of another meeting
   * moves it.
   */
  const [panel, setPanel] = useState<Panel>(initialPanel);

  /* Said on the way in as well as on every switch: 'prep' is a choice too. */
  useEffect(() => {
    onPanelChange?.(panel);
  }, [panel, onPanelChange]);
  /* Which gesprekstype was picked and is still waiting for an answer. */
  const [switching, setSwitching] = useState<PendingTemplate | null>(null);
  const [confirmingRegenerate, setConfirmingRegenerate] = useState<RegenerateAsk | null>(
    null,
  );
  const [typesWanted, setTypesWanted] = useState(false);
  const [attendeesOpen, setAttendeesOpen] = useState(false);
  /* The two things a user may do to a meeting they wrote down themselves. */
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  /* Which document is waiting for a "yes, throw it away". */
  const [deletingDocument, setDeletingDocument] = useState<MeetingPageDocument | null>(
    null,
  );
  /* Whether the page is asking how far taking Wundo off this one reaches. */
  const [removing, setRemoving] = useState(false);
  const [copied, setCopied] = useState(false);
  /*
   * How far the user is into starting a recording: `replace` is the warning
   * that a recording already made here is about to be thrown away, `setup` is
   * the microphone question every recording begins with. Null is neither on
   * screen.
   */
  const [recordAsk, setRecordAsk] = useState<RecordAsk>(null);
  /* And, for a recording the page lost hold of, whether to pick it back up. */
  const [resumeAsk, setResumeAsk] = useState(false);
  /*
   * Whether the running recording has been folded back into its pill. It starts
   * open — a recording is what the user is doing, not a setting — and the pill
   * is the way back to it.
   */
  const [recordMinimized, setRecordMinimized] = useState(false);

  const { data: user } = useQuery({
    queryKey: ['currentUser'],
    queryFn: fetchCurrentUser,
  });

  const userId = user?.id;

  /*
   * Our own support menu: for a superadmin, signed in as themselves or as the
   * user. Only a superadmin can sign in as somebody, and the server refuses
   * everyone else anyway; this only keeps the app from offering it.
   */
  const impersonatedBy = useImpersonatedBy();
  const staff = user?.role === 'superadmin' || Boolean(impersonatedBy);
  const [transcriptOpen, setTranscriptOpen] = useState(false);
  const [topicsOpen, setTopicsOpen] = useState(false);
  /* The share-notes dialog the notetaker banner opens. */
  const [sharingFromIssue, setSharingFromIssue] = useState(false);

  const pageQuery = meetingPageQuery(user?.id ?? '', meetingId);
  const pageKey = pageQuery.queryKey;
  const {
    data: page,
    isPending,
    isError,
  } = useQuery({
    ...pageQuery,
    enabled: Boolean(user?.id),
    /*
     * Nothing tells this page when a verslag is written in the background —
     * from debrief notes, or from a recording — so it asks again until it is
     * there. A running debrief polls before its notes land, or the page would
     * wait on the refetch it was itself supposed to make.
     */
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return false;
      /* While the notetaker may be stuck at the door, the page says so live. */
      if (data.decision.record && notetakerMayBeInCall(data)) return NOTETAKER_POLL_MS;
      if (data.report) return false;
      const pasted = data.materials.some((material) => material.kind === 'pasted');
      const debriefRunning = data.debrief?.conversation_id != null;
      if (pasted || debriefRunning) return REPORT_POLL_MS;
      if (data.report_pending === 'notetaker') return NOTETAKER_POLL_MS;
      return data.report_pending ? REPORT_POLL_MS : false;
    },
  });

  /*
   * Which document the user is reading, if the open tab holds one. A boolean
   * per tab rather than the page, so the polling above never says it twice;
   * the server keeps it to once a day.
   */
  const hasPrep = (page?.prep?.content.text.trim().length ?? 0) > 0;
  const hasReport = page?.report != null;
  const shownDocument =
    panel === 'prep' ? (hasPrep ? 'prep' : null) : hasReport ? 'report' : null;
  useEffect(() => {
    if (!userId || !shownDocument) return;
    void reportMeetingDocumentOpened(userId, meetingId, shownDocument);
  }, [userId, meetingId, shownDocument]);

  /*
   * The editor's copy and its upload path, one object for every surface on the
   * page. Keyed on the ids, not on the objects behind them: a refetched profile
   * or page is not a reason to rewire the documents.
   *
   * The thread goes in because a file dropped into a document is not a stray
   * attachment: on a conversation, it is a document of that conversation, and
   * the panel above has to hear about it the moment it lands.
   */
  const threadId = page?.thread?.id ?? null;
  const refreshPage = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['meetingPage'] });
  }, [queryClient]);
  const editorWiring = useMemo(
    () =>
      userId
        ? buildRichTextEditorWiring({
            userId,
            threadId,
            onDocumentAdded: refreshPage,
          })
        : null,
    [userId, threadId, refreshPage],
  );

  /*
   * Same again for the gesprekstype: fetched when the menu in the header is
   * first opened, and when the edit dialog — which asks the same question of a
   * meeting the user wrote down themselves — goes on screen.
   */
  const { data: templates = [] } = useQuery({
    queryKey: ['meetingTemplates', user?.id, 'use'],
    queryFn: () => fetchMeetingTemplates(user!.id, 'use'),
    enabled: Boolean(user?.id) && (typesWanted || editing),
  });

  /*
   * The names already known, for the suggestions behind the "who is it with"
   * field. Only manual meetings reach this dialog, and a manual meeting owns
   * its participants whether or not a calendar has been connected since —
   * hiding the field would strand the names already on it. Read while the
   * dialog is open, like the gesprekstypes above.
   */
  const { data: colleagues = [] } = useQuery({
    queryKey: ['colleagues', user?.id, 'active'],
    queryFn: () => fetchColleagues(user!.id, { hidden: false }),
    enabled: Boolean(user?.id) && editing,
  });

  /*
   * The server decides what changed. A correction can create a thread, so
   * nothing here is worth guessing at locally.
   *
   * The agenda is invalidated too: adding this conversation to Wundo is exactly
   * the kind of change that redraws the card the user came in from.
   */
  const act = useMutation({
    mutationFn: (run: () => Promise<void>) => run(),
    onSuccess: async () => {
      setFlip(null);
      setSwitching(null);
      setConfirmingRegenerate(null);
      setDeletingDocument(null);
      setRemoving(false);
      await queryClient.invalidateQueries({ queryKey: ['meetingPage'] });
      await queryClient.invalidateQueries({ queryKey: ['meetings'] });
    },
  });

  const run = (task: () => Promise<void>) => act.mutate(task);

  /*
   * Editing and deleting a manual meeting stand apart from `act`: each has a
   * dialog of its own that has to stay open and say so when it fails, where
   * `act` closes every dialog on the way out.
   */
  const editMeeting = useMutation({
    mutationFn: (values: ManualMeetingValues) =>
      updateManualMeeting(user!.id, meetingId, {
        title: values.title,
        starts_at: values.startsAt,
        /*
         * Stored exactly as the pill in the header stores it, and without
         * rewriting the preparation: replacing a document the user may already
         * have edited is a question of its own, asked there.
         */
        template_id: values.templateId,
        /*
         * The whole list, always: the dialog hands back the chips it now has,
         * so a name taken off is a name gone.
         */
        attendees: values.attendees.map((name) => ({ name })),
      }),
    onSuccess: async () => {
      setEditing(false);
      await queryClient.invalidateQueries({ queryKey: ['meetingPage'] });
      await queryClient.invalidateQueries({ queryKey: ['meetings'] });
      /* A name tagged here is a colleague from now on; the team page has to hear it. */
      await queryClient.invalidateQueries({ queryKey: ['colleagues'] });
    },
  });

  const removeMeeting = useMutation({
    mutationFn: () => deleteManualMeeting(user!.id, meetingId),
    onSuccess: async () => {
      setDeleting(false);
      /*
       * Off the screen first, then the week is read again. The other order
       * refetches this meeting's page while it is still on screen, and answers
       * the person who just deleted it with a load failure.
       */
      onLeave?.();
      await queryClient.invalidateQueries({ queryKey: ['meetings'] });
      await queryClient.invalidateQueries({ queryKey: ['meetingPage'] });
    },
  });

  /*
   * Adding a document stands apart from `act` as well: the panel it is added
   * from stays open, and a file that was refused is answered inside it rather
   * than by every dialog on the page closing.
   */
  const uploadDocument = useMutation({
    mutationFn: ({ thread, file }: { thread: string; file: File }) =>
      uploadThreadDocument(user!.id, thread, file),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['meetingPage'] });
    },
  });

  /*
   * Generation is its own mutation rather than part of `act`: the preparation
   * fires it by itself on first open, and a failure there is a retry button on
   * the panel rather than an error under a dialog nobody opened.
   */
  const generation = useMutation({
    mutationFn: async ({
      panel: which,
      regenerate,
    }: GenerateRequest): Promise<MeetingPagePrep | null> => {
      if (which === 'prep') {
        return generateMeetingPrep(user!.id, meetingId, regenerate);
      }
      await generateMeetingReport(user!.id, meetingId, regenerate);
      return null;
    },
    /*
     * The preparation comes back with the answer, so it is written straight
     * into the page the user is already looking at. Asking for the whole page
     * again would put a second wait on top of the one they just sat through,
     * and nothing else about the meeting moved. The verslag has no such answer
     * yet — it still reads the page again.
     */
    onSuccess: async (prep) => {
      setConfirmingRegenerate(null);
      if (prep) {
        queryClient.setQueryData<MeetingPageResponse>(pageKey, (current) =>
          current ? { ...current, prep } : current,
        );
        return;
      }
      await queryClient.invalidateQueries({ queryKey: ['meetingPage'] });
    },
  });

  /*
   * The share-notes dialog's one gesture: keep the notes and write from them.
   * Two requests behind one button, because the intermediate "note saved, now
   * generate" state is machinery the user never asked to see.
   */
  const pasteGenerate = useMutation({
    mutationFn: async (content: string) => {
      await saveMeetingNote(user!.id, meetingId, content);
      await generateMeetingReport(user!.id, meetingId, false);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['meetingPage'] });
    },
  });

  /*
   * Autosaving deliberately does not invalidate: the panel already holds the
   * document the user is typing into, and a refetch mid-sentence would put the
   * server's older copy back under the caret.
   */
  const savePrep = useMutation({
    mutationFn: (content: MeetingPrepContent) =>
      saveMeetingPrep(user!.id, meetingId, content),
  });

  const saveReport = useMutation({
    mutationFn: (sections: MeetingReportSection[]) =>
      saveMeetingReport(user!.id, meetingId, sections),
  });

  /*
   * The microphone, held here rather than inside a panel: the pill that says a
   * recording is running, the dialogs that start and end one, and the tab that
   * waits for the verslag are all on this page, and there is only ever one
   * recording behind them.
   */
  const recording = useOfflineRecording({
    userId: userId ?? '',
    meetingId,
    offline: page?.offline_recording ?? NO_OFFLINE_RECORDING,
    onChanged: refreshPage,
  });
  const recordingState = recording.state;

  /*
   * The way out of this page, while this device is holding the microphone.
   * Every link on it — the rail, the way back to the conversations — unmounts
   * the recorder, so the navigation is held until the user has said what should
   * happen to the recording. Closing the tab is a different door with a
   * different lock: the browser's own warning, which the recorder hook puts up.
   */
  const leaving = useBlocker({
    shouldBlockFn: LEAVING_COSTS_THE_RECORDING,
    disabled: recordingState !== 'recording',
    enableBeforeUnload: false,
    withResolver: true,
  });

  /*
   * Both are stable across renders, which is what lets the preparation's
   * first-open generation sit in an effect without re-firing on every
   * keystroke.
   */
  const startGeneration = generation.mutate;
  const onGeneratePrep = useCallback(
    () => startGeneration({ panel: 'prep', regenerate: false }),
    [startGeneration],
  );

  /*
   * The clipboard is the browser's; everything else about sharing is the
   * page's. Both documents share the one act — each hands over its own text.
   */
  const copy = (text: string) => {
    void navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), COPIED_FOR_MS);
  };

  if (isPending || !user || !editorWiring) {
    return <p className='text-base text-ink-muted'>{m.meeting_loading()}</p>;
  }
  if (isError) {
    return <p className='text-base text-danger'>{m.meeting_load_failed()}</p>;
  }

  const chooseScope = (scope: MeetingCorrectionScope) => {
    if (flip === null) return;
    run(() => correctMeeting(user.id, meetingId, { record: flip, scope }));
  };

  /*
   * Whether writing the preparation again with a new gesprekstype would work.
   * Where it would not — the meeting is over, Wundo knows nothing to write
   * from, or an intake is still open — the pick saves without that question.
   */
  const rewritable = page.prep_available;
  /*
   * Whether picking a gesprekstype is worth a scope question. Only a
   * conversation with more than one meeting in it has two different answers to
   * give — on a single meeting, "this one" and "all of them" are the same
   * thing said twice.
   */
  const scopeWorthAsking = (page.thread?.meeting_count ?? 0) > 1;

  /* Saving the pick, and whether to write the preparation again with it. */
  const saveTemplate = (picked: PendingTemplate, recreate: boolean) =>
    run(() =>
      setMeetingTemplate(
        user.id,
        meetingId,
        picked.template.id,
        recreate,
        picked.scope ?? 'occurrence',
      ),
    );

  /*
   * A gesprekstype was picked. Which questions follow is the meeting's
   * business: how far the choice reaches, when the conversation has more
   * meetings than this one, and whether to write the topics again, when there
   * is still a preparation to write. With neither to ask, the pick saves.
   */
  const pickTemplate = (template: MeetingTypeOption) => {
    if (scopeWorthAsking) {
      setSwitching({ template, scope: null });
      return;
    }
    if (!rewritable) {
      saveTemplate({ template, scope: 'occurrence' }, false);
      return;
    }
    setSwitching({ template, scope: 'occurrence' });
  };

  /* The answer to the first question, which is the last one where nothing can be rewritten. */
  const chooseTemplateScope = (scope: MeetingTemplateScope) => {
    if (!switching) return;
    if (!rewritable) {
      saveTemplate({ ...switching, scope }, false);
      return;
    }
    setSwitching({ ...switching, scope });
  };

  /* How far a choice about Wundo's help reaches — the same rule the agenda uses. */
  const scope = wundoScope(page.decision.never_automatic);

  /*
   * Let Wundo into this conversation, exactly as the agenda's "+ Wundo" does:
   * no question, the whole series, and this one occurrence only where a
   * standing choice is refused. The user stays here — they came to this page
   * for the preparation, which is what appears once Wundo is in.
   */
  const addToWundo = () => correctMeeting(user.id, meetingId, { follow: true, scope });

  /*
   * And the way back out. Unlike adding, this one is asked first: how far it
   * reaches is the whole difference between skipping one sitting and ending a
   * standing arrangement, and neither answer is obviously the meant one.
   */
  const removeFromWundo = (chosen: MeetingCorrectionScope) =>
    run(() => correctMeeting(user.id, meetingId, { follow: false, scope: chosen }));

  const attendees = meetingAttendeeList(page.meeting);
  /*
   * A meeting the user wrote down themselves. Half this page is about what a
   * calendar said — who is coming, whether Wundo helps — and a manual meeting
   * answers neither: nobody is on it, and Wundo helps because it was asked to.
   */
  const manual = page.meeting.source === 'manual';
  /*
   * What Wundo carries into this conversation from the ones before it. Only the
   * last of them can be named here — its verslag is the recap — but the count
   * is what the control has to say, because "how much does it know about me?"
   * is the question, not "which one exactly".
   */
  const earlier = page.thread?.past_meeting_count ?? 0;
  /* Read once, so that what the panel offers and what it acts on agree. */
  const thread = page.thread;
  const typeOptions = withCurrentTemplate(templates, page.template);
  /* The menu offers this meeting's kind; one the user wrote down takes any. */
  const kindOptions = withCurrentTemplate(
    templates.filter(
      (template) => page.template_kind === null || template.kind === page.template_kind,
    ),
    page.template,
  );

  /*
   * A recording that is running, or one this page lost hold of, gets a control
   * of its own — red, in the settings row, saying the clock out loud. Every
   * other state of the microphone is an offer, and offers live in the panel.
   */
  const running = recordingState === 'recording' || recordingState === 'interrupted';
  const offlineOffer: MeetingOfflineOffer = running ? null : recordingState;
  /*
   * Nothing to switch and nothing to record is nothing to open a panel for —
   * and an organisation without the calendar integration has no notetaker to
   * switch at all. The microphone keeps its own offer on the page below.
   */
  const notesWorthShowing =
    user.calendar_enabled && page.decision.follow && (!manual || offlineOffer !== null);
  /*
   * The offer moved onto the page itself, for the meeting where the panel's
   * other half is empty: no online link, so no notetaker, so the microphone is
   * the whole of what Wundo can do here. Gone once there is something to show
   * for it — a transcript that landed, or the verslag written from it.
   */
  const offerRecordingHere =
    page.decision.follow &&
    !page.meeting.join_url &&
    recordingState === 'idle' &&
    !page.report &&
    !page.materials.some((material) => material.kind === 'transcript');

  const recordPending = recording.starting || recording.stopping;
  /*
   * What the verslag tab waits on: the server's answer, plus what only this
   * page knows yet — a write it asked for, or a stop the server has not heard.
   */
  const reportWaiting: MeetingReportWaiting | null =
    generation.isPending || pasteGenerate.isPending || page.report_pending === 'debrief'
      ? 'writing'
      : recording.stopping || page.report_pending === 'microphone'
        ? 'microphone'
        : page.report_pending === 'notetaker'
          ? 'notetaker'
          : null;
  /*
   * A recording that has just started opens in front of the page: the question
   * that was on screen is answered, and what replaces it is the recording
   * itself rather than the page the user was on before.
   */
  const startRecording = (deviceId: string | null) =>
    recording.start(
      deviceId,
      () => {
        setRecordAsk(null);
        setRecordMinimized(false);
      },
      () => setRecordAsk(null),
    );
  /*
   * Asking to record. On a meeting that already has a recording the cost comes
   * first; everywhere else the microphone question is the whole of it.
   */
  const askToRecord = () => setRecordAsk(recordingState === 'done' ? 'replace' : 'setup');
  /*
   * Resuming takes the microphone that was chosen last, without asking again:
   * the question was already answered once for this recording, and asking it a
   * second time is a step between the user and audio they are losing.
   */
  const resumeRecording = () =>
    recording.start(
      storedMicrophoneId(),
      () => {
        setResumeAsk(false);
        setRecordMinimized(false);
      },
      () => setResumeAsk(false),
    );
  /* Stopping is asking for the verslag, so the tab it lands on comes forward. */
  const stopRecording = () => {
    recording.stop();
    setPanel('report');
  };
  /*
   * Leaving after all. The microphone is let go of first, so what was said up
   * to here becomes a verslag rather than a recording nobody ever closed.
   */
  const leaveWhileRecording = () => {
    recording.stop();
    leaving.proceed?.();
  };

  return (
    <div className='@container'>
      {/*
        The navy bar is the page's title — there is no plain heading above it.
        It carries what the meeting is and the acts that are about the meeting
        as a whole; what it can be set to stands in the row under it.
      */}
      <HeroBar>
        <HeroBody>
          {/*
            The journey step this conversation is, beside its name and leading
            to the journey: the same pink label the agenda gives it.
          */}
          <div className='flex flex-wrap items-center gap-x-2.5 gap-y-1'>
            <HeroTitle>{meetingPageTitle(page)}</HeroTitle>
            {page.journey && (
              <Link
                to='/journeys/$journeyId'
                params={{ journeyId: page.journey.journey_id }}
                data-slot='meeting-journey-label'
                className={typePillVariants({
                  type: 'journey',
                  size: 'sm',
                  interactive: true,
                })}
              >
                {m.meeting_journey_label({
                  name: page.journey.type_name,
                  position: page.journey.step_position,
                  count: page.journey.step_count,
                })}
              </Link>
            )}
          </div>
          <HeroMeta>
            <span>{whenOf(page)}</span>
            {/*
              A handful of names, the organiser among them and marked. Past
              that the line stops naming people and offers the rest — a
              two-hundred-person invitation spelled out here says nothing, and
              the one name that always matters would be lost in it.
            */}
            {attendees.all.length > 0 && (
              <>
                <HeroMetaSeparator />
                <span>{namedAttendees(attendees.named)}</span>
                {attendees.hidden > 0 && (
                  <HeroMetaAction onClick={() => setAttendeesOpen(true)}>
                    {m.meeting_attendees_more({ count: attendees.hidden })}
                  </HeroMetaAction>
                )}
              </>
            )}
          </HeroMeta>
        </HeroBody>
        <HeroActions>
          {/* Where the conversation actually happens, when it happens online. */}
          {page.meeting.join_url && (
            <a
              className={buttonVariants({ size: 'sm', variant: 'onPrimary' })}
              href={page.meeting.join_url}
              target='_blank'
              rel='noreferrer'
            >
              <Video aria-hidden='true' className='size-3.5' />
              {m.meeting_join()}
            </a>
          )}
          {/*
            Only a meeting the user wrote down is theirs to change. A calendar
            meeting is changed in the calendar, and offering it here would be
            offering something the server refuses.
          */}
          {manual && (
            <>
              <Button size='sm' variant='onPrimary' onClick={() => setEditing(true)}>
                {m.manual_meeting_edit()}
              </Button>
              <Button size='sm' variant='onPrimary' onClick={() => setDeleting(true)}>
                {m.manual_meeting_delete()}
              </Button>
            </>
          )}
          {/*
            A conversation Wundo stays out of has one thing to say about
            itself, and it belongs on the bar rather than among the settings
            under it: there is nothing to set yet. Adding is the whole page.
          */}
          {!manual && !page.decision.follow && (
            <Button
              size='sm'
              variant='onPrimary'
              disabled={act.isPending}
              onClick={() => run(addToWundo)}
            >
              {m.meeting_add_to_wundo()}
            </Button>
          )}
        </HeroActions>
      </HeroBar>

      {/*
        Under the bar, not above it: the bar is the title now. It goes back to
        the conversations, which is the list this page is one entry of — the
        agenda is where the meeting was picked up, not where it lives.
      */}
      <Link to='/conversations' className={cn(backLinkVariants(), 'mt-3.5')}>
        <ChevronLeft aria-hidden='true' />
        {m.meeting_back_to_conversations()}
      </Link>

      {/*
        What this conversation is set to: its gesprekstype, what Wundo does
        here, and what Wundo reads along. Three settings side by side, each
        saying its own state — no panel to open first, and nothing here that
        takes the user off the page.
      */}
      <HeroControlRow className='mt-4.5'>
        {/*
          Where the preparation cannot be written again, the recreate question
          is moot, so only the scope is asked, if anything, and the new
          gesprekstype shapes the next document written.
        */}
        <MeetingTypeControl
          current={page.template}
          templates={kindOptions}
          onOpen={() => setTypesWanted(true)}
          onPick={pickTemplate}
        />
        {/*
          The one choice that stands on top of following. Nothing on a meeting
          the user wrote down — Wundo helps because they wrote it down, and the
          notetaker has no meeting to join — and nothing on one Wundo stays out
          of, where the bar above offers the only thing there is to offer.
        */}
        {notesWorthShowing && (
          <MeetingNotesControl
            on={page.decision.record}
            hasOnlineLink={Boolean(page.meeting.join_url)}
            manual={manual}
            offline={offlineOffer}
            canRecord={recording.supported}
            pending={act.isPending || recordPending}
            onFlip={setFlip}
            onStartRecording={askToRecord}
          />
        )}
        {/*
          The one control that puts itself on the row: a recording nobody may
          discover afterwards, and the interrupted one that still holds audio.
        */}
        {running && (
          <OfflineRecordingControl
            live={recordingState === 'recording'}
            elapsed={recording.elapsed}
            chunkLost={recording.chunkLost}
            pending={recordPending}
            onOpen={() => setRecordMinimized(false)}
            onStop={stopRecording}
            onResume={() => setResumeAsk(true)}
          />
        )}
        {/*
          On every conversation, however new. What Wundo reads along is not
          only what it has been through here — it is also whatever the user
          hands it, and this panel is where that is handed over. Off a meeting
          in no conversation at all, because a document has nowhere to live yet.
        */}
        {thread && (
          <MeetingSourcesControl
            earlier={earlier}
            previous={page.recap}
            name={meetingPageTitle(page)}
            documents={page.documents}
            uploading={uploadDocument.isPending}
            uploadFailed={uploadDocument.isError}
            onAddDocument={(file) => uploadDocument.mutate({ thread: thread.id, file })}
            onDeleteDocument={setDeletingDocument}
          />
        )}
        {/*
          What is left to do here, at the end of the row: today, taking Wundo
          back off. On the same conversations the notetaker's control is on —
          there has to be something to take away before it can be offered.
        */}
        {!manual && page.decision.follow && (
          <MeetingMoreControl
            onRemove={() => {
              act.reset();
              setRemoving(true);
            }}
          />
        )}
        {staff && (
          <MeetingStaffControl
            onTranscript={() => setTranscriptOpen(true)}
            onTopics={() => setTopicsOpen(true)}
          />
        )}
      </HeroControlRow>

      {offerRecordingHere && (
        <OfflineRecordingBanner
          supported={recording.supported}
          pending={recordPending}
          onStart={askToRecord}
        />
      )}

      {page.notetaker_issue && (
        <NotetakerIssueBanner
          issue={page.notetaker_issue}
          meeting={page.meeting}
          onShareNotes={() => setSharingFromIssue(true)}
        />
      )}

      <div className='mt-4.5'>
        <div className='min-w-0'>
          {/*
            Pills, not underlines: these switch between two views of one
            meeting, where the section tabs elsewhere switch between parts of
            a page. Content sections underline; object views pill.
          */}
          <ViewTabList>
            <ViewTab active={panel === 'prep'} onClick={() => setPanel('prep')}>
              {m.meeting_tab_prep()}
            </ViewTab>
            <ViewTab active={panel === 'report'} onClick={() => setPanel('report')}>
              {m.meeting_tab_report()}
            </ViewTab>
          </ViewTabList>
          {panel === 'prep' ? (
            <MeetingPrepPanel
              page={page}
              generating={generation.isPending}
              generateFailed={generation.isError}
              copied={copied}
              editor={editorWiring}
              onGenerate={onGeneratePrep}
              onRegenerate={() => setConfirmingRegenerate({ panel: 'prep' })}
              onSave={savePrep.mutate}
              onCopy={() => copy(page.prep?.content?.text ?? '')}
              onToggleActionItem={(itemId, done) =>
                run(() => updateMeetingActionItem(user.id, meetingId, itemId, { done }))
              }
              onEditActionItem={(itemId, text) =>
                run(() => updateMeetingActionItem(user.id, meetingId, itemId, { text }))
              }
              onAddActionItem={(text) =>
                run(() => addMeetingActionItem(user.id, meetingId, text))
              }
              onDeleteActionItem={(itemId) =>
                run(() => deleteMeetingActionItem(user.id, meetingId, itemId))
              }
            />
          ) : (
            <MeetingReportPanel
              page={page}
              waiting={reportWaiting}
              recordingFailed={offlineOffer === 'failed'}
              generating={reportWaiting === 'writing'}
              generateFailed={generation.isError || pasteGenerate.isError}
              copied={copied}
              editor={editorWiring}
              onGenerate={() => generation.mutate({ panel: 'report', regenerate: false })}
              onPasteGenerate={pasteGenerate.mutate}
              /*
               * The note's full text, fetched only when the dialog opens —
               * the page carries an excerpt, and a 50k paste has no business
               * riding along on every read.
               */
              onLoadNote={async () => {
                const note = page.materials.find(
                  (material) => material.kind === 'pasted',
                );
                return note ? fetchMeetingNote(user.id, meetingId, note.id) : null;
              }}
              onRegenerate={() => setConfirmingRegenerate({ panel: 'report' })}
              onSave={saveReport.mutate}
              onDeleteMaterial={(materialId) =>
                run(() => deleteMeetingMaterial(user.id, meetingId, materialId))
              }
              onCopy={() => copy(renderMeetingReport(page.report?.sections ?? []))}
              onToggleActionItem={(itemId, done) =>
                run(() => updateMeetingActionItem(user.id, meetingId, itemId, { done }))
              }
              onEditActionItem={(itemId, text) =>
                run(() => updateMeetingActionItem(user.id, meetingId, itemId, { text }))
              }
              onAddActionItem={(text) =>
                run(() => addMeetingActionItem(user.id, meetingId, text))
              }
              onDeleteActionItem={(itemId) =>
                run(() => deleteMeetingActionItem(user.id, meetingId, itemId))
              }
            />
          )}
        </div>
      </div>

      <ScopeDialog
        open={flip !== null}
        thread={page.thread}
        kind={page.decision.kind}
        occurrenceOnly={page.decision.never_automatic}
        failed={act.isError}
        pending={act.isPending}
        onChoose={chooseScope}
        onCancel={() => {
          setFlip(null);
          act.reset();
        }}
      />

      <MeetingRemoveDialog
        open={removing}
        thread={page.thread}
        kind={page.decision.kind}
        occurrenceOnly={page.decision.never_automatic}
        failed={act.isError}
        pending={act.isPending}
        onChoose={removeFromWundo}
        onCancel={() => {
          setRemoving(false);
          act.reset();
        }}
      />

      {/*
        The same question the switches ask, in front of the same dialog: a
        gesprekstype for this one meeting, or for every conversation with this
        person. Only asked where the two answers differ.
      */}
      <ScopeDialog
        open={switching !== null && switching.scope === null}
        thread={page.thread}
        kind={page.decision.kind}
        occurrenceOnly={false}
        failed={act.isError}
        pending={act.isPending}
        onChoose={chooseTemplateScope}
        onCancel={() => {
          setSwitching(null);
          act.reset();
        }}
      />

      <MeetingTemplateSwitchDialog
        template={switching?.scope ? switching.template : null}
        failed={act.isError}
        pending={act.isPending}
        onChoose={(recreate) => switching && saveTemplate(switching, recreate)}
        onCancel={() => {
          setSwitching(null);
          act.reset();
        }}
      />

      {/*
        One dialog, three promises. A preparation keeps every line the user
        wrote or edited; a verslag keeps nothing, because prose cannot be
        merged. Saying so is the whole point of asking — and material added to
        a verslag that exists asks the same question, in its own words.
      */}
      <MeetingRegenerateDialog
        open={confirmingRegenerate !== null}
        title={regenerateTitle(confirmingRegenerate)}
        body={regenerateBody(confirmingRegenerate)}
        confirmLabel={regenerateConfirmLabel(confirmingRegenerate)}
        failed={generation.isError}
        pending={generation.isPending}
        onConfirm={() =>
          confirmingRegenerate &&
          generation.mutate({
            panel: confirmingRegenerate.panel,
            regenerate: true,
          })
        }
        onCancel={() => {
          setConfirmingRegenerate(null);
          generation.reset();
        }}
      />

      <ManualMeetingDialog
        open={editing}
        title={m.manual_meeting_edit()}
        saveLabel={m.manual_meeting_save()}
        initial={{
          title: page.meeting.title ?? '',
          startsAt: new Date(page.meeting.starts_at),
          templateId: page.template?.id ?? undefined,
          /*
           * The names as the page already shows them. A manual meeting's guests
           * are names and nothing else, so the label is the whole answer.
           */
          attendees: page.meeting.attendees.map((attendee) => attendee.label),
        }}
        templates={typeOptions}
        withPeople={{ suggestions: colleagues.map((colleague) => colleague.label) }}
        pending={editMeeting.isPending}
        failed={editMeeting.isError}
        onSubmit={(values) => editMeeting.mutate(values)}
        onCancel={() => {
          setEditing(false);
          editMeeting.reset();
        }}
      />

      <ManualMeetingDeleteDialog
        open={deleting}
        pending={removeMeeting.isPending}
        failed={removeMeeting.isError}
        onConfirm={() => removeMeeting.mutate()}
        onCancel={() => {
          setDeleting(false);
          removeMeeting.reset();
        }}
      />

      <MeetingDocumentDeleteDialog
        document={deletingDocument}
        failed={act.isError}
        pending={act.isPending}
        onConfirm={() =>
          deletingDocument &&
          thread &&
          run(() => deleteThreadDocument(user.id, thread.id, deletingDocument.id))
        }
        onCancel={() => {
          setDeletingDocument(null);
          act.reset();
        }}
      />

      <MeetingAttendeesDialog
        open={attendeesOpen}
        attendees={attendees.all}
        onClose={() => setAttendeesOpen(false)}
      />

      {/*
        The moments of a microphone recording that need an answer: which
        microphone, whether to pick an interrupted one back up, three hours in
        whether anybody is still there, and — on the way to another page —
        whether the recording may end here.
      */}
      <OfflineRecordingReplaceDialog
        open={recordAsk === 'replace'}
        onConfirm={() => setRecordAsk('setup')}
        onCancel={() => setRecordAsk(null)}
      />

      <OfflineRecordingStartDialog
        open={recordAsk === 'setup'}
        pending={recording.starting}
        failed={recording.startFailed}
        onStart={startRecording}
        onCancel={() => setRecordAsk(null)}
      />

      {/*
        And in between the two: the recording itself, holding the page for as
        long as it runs, unless the user folds it back into its pill. It steps
        aside for the three-hour question rather than stacking under it — that
        question is about this recording, so it takes the same place.
      */}
      <OfflineRecordingLiveDialog
        open={recordingState === 'recording' && !recordMinimized && !recording.capReached}
        elapsed={recording.elapsed}
        startedAt={recording.startedAt}
        device={recording.deviceLabel}
        chunkLost={recording.chunkLost}
        pending={recordPending}
        onStop={stopRecording}
        onMinimize={() => setRecordMinimized(true)}
      />

      <OfflineRecordingLeaveDialog
        open={leaving.status === 'blocked'}
        onStay={() => leaving.reset?.()}
        onLeave={leaveWhileRecording}
      />

      <OfflineRecordingResumeDialog
        open={resumeAsk}
        pending={recording.starting}
        failed={recording.startFailed}
        onConfirm={resumeRecording}
        onCancel={() => setResumeAsk(false)}
      />

      <OfflineRecordingCapDialog
        open={recording.capReached}
        onContinue={recording.keepGoing}
        onStop={stopRecording}
      />

      {/* Only offered while there is no note yet, so it always starts empty. */}
      {sharingFromIssue && (
        <MeetingShareNotesDialog
          open
          pending={pasteGenerate.isPending}
          initial={null}
          submitLabel={m.meeting_report_generate()}
          onSubmit={(content) => {
            setSharingFromIssue(false);
            setPanel('report');
            pasteGenerate.mutate(content);
          }}
          onCancel={() => setSharingFromIssue(false)}
        />
      )}

      <MeetingTranscriptDialog
        open={transcriptOpen}
        userId={user.id}
        meetingId={meetingId}
        onClose={() => setTranscriptOpen(false)}
      />
      <MeetingTopicJudgmentDialog
        open={topicsOpen}
        userId={user.id}
        meetingId={meetingId}
        onClose={() => setTopicsOpen(false)}
      />
    </div>
  );
}

/*
 * The two ways of asking "again?", one per document, both keeping the same
 * promise: the dialog says what is lost before anything is.
 */
function regenerateTitle(ask: RegenerateAsk | null): string {
  return ask?.panel === 'report'
    ? m.meeting_report_regenerate_title()
    : m.meeting_prep_regenerate_title();
}

function regenerateBody(ask: RegenerateAsk | null): string {
  return ask?.panel === 'report'
    ? m.meeting_report_regenerate_body()
    : m.meeting_prep_regenerate_body();
}

function regenerateConfirmLabel(ask: RegenerateAsk | null): string {
  return ask?.panel === 'report'
    ? m.meeting_report_regenerate_save()
    : m.meeting_prep_regenerate_save();
}

/*
 * From a little before the start until the end: the notetaker may be waiting in
 * the call, and nothing else tells this page when it starts to.
 */
function notetakerMayBeInCall(page: MeetingPageResponse): boolean {
  const now = Date.now();
  return (
    Boolean(page.meeting.join_url) &&
    now >= new Date(page.meeting.starts_at).getTime() - NOTETAKER_EARLY_MS &&
    now < new Date(page.meeting.ends_at).getTime()
  );
}

/** "15 juni · 10:00 – 10:30", in the reader's own locale. */
function whenOf(page: MeetingPageResponse): string {
  const day = formatDayAndMonth(new Date(page.meeting.starts_at));
  return `${day} · ${formatTime(page.meeting.starts_at)} – ${formatTime(page.meeting.ends_at)}`;
}

/** "Toon (organisator), Bea, Chris" — the names the hero line carries. */
function namedAttendees(named: MeetingAttendeeRow[]): string {
  return named
    .map((attendee) =>
      attendee.organizer
        ? m.meeting_attendee_organizer_inline({ name: attendee.label })
        : attendee.label,
    )
    .join(', ');
}
